import api from '../../lib/api';
import type {
  AgentAnalyticsResponse,
  AnalyticsPeriod,
  AudioUrlResponse,
  CallStats,
  CountryLanguageMapping,
  CreateCountryLanguageMappingDto,
  CreateVoiceAgentDto,
  KnowledgeBaseItem,
  ListVoiceAgentsFilters,
  ListVoiceCallsFilters,
  PaginatedVoiceCalls,
  PreviewConfigResponse,
  PreviewToolCallLogEntry,
  PreviewToolCallResponse,
  PreviewTranscriptEntry,
  PromptVariableDefinition,
  ReplayData,
  ReprocessAnalysisResponse,
  StartPreviewResponse,
  TriggerBatchVoiceCallDto,
  TriggerVoiceCallDto,
  UpdateCountryLanguageMappingDto,
  UpdateVoiceAgentDto,
  UpdateVoiceCallDto,
  UploadKnowledgeItemDto,
  UseCaseTemplate,
  VoiceAgent,
  VoiceAgentVersion,
  VoiceCall,
  VoiceCallTranscriptEntry,
  VoiceSummary,
} from './types';

const BASE = '/voice-agents';

export const voiceAgentsApi = {
  list: (organizationId: string, filters: ListVoiceAgentsFilters = {}): Promise<VoiceAgent[]> =>
    api.get<VoiceAgent[]>(BASE, { params: { organizationId, ...filters } }).then((r) => r.data),

  // `language` selects the curated copy: the templates and the fallback text a
  // variable advertises are written per agent language (FLOSEAI-663).
  listTemplates: (language?: string): Promise<UseCaseTemplate[]> =>
    api
      .get<UseCaseTemplate[]>(`${BASE}/templates`, {
        params: language ? { language } : undefined,
      })
      .then((r) => r.data),

  listScriptVariables: (useCase?: string, language?: string): Promise<PromptVariableDefinition[]> =>
    api
      .get<PromptVariableDefinition[]>(`${BASE}/script-variables`, {
        params: { ...(useCase ? { useCase } : {}), ...(language ? { language } : {}) },
      })
      .then((r) => r.data),

  listVoices: (language?: string): Promise<VoiceSummary[]> =>
    api
      .get<VoiceSummary[]>(`${BASE}/voices`, { params: language ? { language } : undefined })
      .then((r) => r.data),

  getAnalytics: (
    id: string,
    organizationId: string,
    period: AnalyticsPeriod,
  ): Promise<AgentAnalyticsResponse> =>
    api
      .get<AgentAnalyticsResponse>(`${BASE}/${id}/analytics`, {
        params: { organizationId, period },
      })
      .then((r) => r.data),

  get: (id: string, organizationId: string): Promise<VoiceAgent> =>
    api.get<VoiceAgent>(`${BASE}/${id}`, { params: { organizationId } }).then((r) => r.data),

  create: (organizationId: string, dto: CreateVoiceAgentDto): Promise<VoiceAgent> =>
    api.post<VoiceAgent>(BASE, dto, { params: { organizationId } }).then((r) => r.data),

  update: (id: string, organizationId: string, dto: UpdateVoiceAgentDto): Promise<VoiceAgent> =>
    api.patch<VoiceAgent>(`${BASE}/${id}`, dto, { params: { organizationId } }).then((r) => r.data),

  // FLOSEAI-656 — its own endpoint, not PATCH :id: renaming works on a
  // published agent, editing its configuration does not.
  rename: (id: string, organizationId: string, name: string): Promise<VoiceAgent> =>
    api
      .patch<VoiceAgent>(`${BASE}/${id}/name`, { name }, { params: { organizationId } })
      .then((r) => r.data),

  publish: (id: string, organizationId: string): Promise<VoiceAgent> =>
    api
      .post<VoiceAgent>(`${BASE}/${id}/publish`, undefined, { params: { organizationId } })
      .then((r) => r.data),

  archive: (id: string, organizationId: string): Promise<VoiceAgent> =>
    api
      .post<VoiceAgent>(`${BASE}/${id}/archive`, undefined, { params: { organizationId } })
      .then((r) => r.data),

  duplicate: (id: string, organizationId: string): Promise<VoiceAgent> =>
    api
      .post<VoiceAgent>(`${BASE}/${id}/duplicate`, undefined, { params: { organizationId } })
      .then((r) => r.data),

  remove: (id: string, organizationId: string): Promise<void> =>
    api.delete(`${BASE}/${id}`, { params: { organizationId } }).then(() => undefined),

  listVersions: (id: string, organizationId: string): Promise<VoiceAgentVersion[]> =>
    api
      .get<VoiceAgentVersion[]>(`${BASE}/${id}/versions`, { params: { organizationId } })
      .then((r) => r.data),

  uploadKnowledgeItem: (
    id: string,
    organizationId: string,
    dto: UploadKnowledgeItemDto,
  ): Promise<KnowledgeBaseItem> =>
    api
      .post<KnowledgeBaseItem>(`${BASE}/${id}/kb`, dto, { params: { organizationId } })
      .then((r) => r.data),

  uploadKnowledgePdf: (
    id: string,
    organizationId: string,
    formData: FormData,
  ): Promise<KnowledgeBaseItem> =>
    api
      .post<KnowledgeBaseItem>(`${BASE}/${id}/kb/pdf`, formData, {
        params: { organizationId },
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data),

  removeKnowledgeItem: (id: string, itemId: string, organizationId: string): Promise<void> =>
    api.delete(`${BASE}/${id}/kb/${itemId}`, { params: { organizationId } }).then(() => undefined),

  startPreview: (id: string, organizationId: string): Promise<StartPreviewResponse> =>
    api
      .post<StartPreviewResponse>(`${BASE}/${id}/preview/start`, undefined, {
        params: { organizationId },
      })
      .then((r) => r.data),

  getPreviewConfig: (
    id: string,
    organizationId: string,
    sessionId: string,
  ): Promise<PreviewConfigResponse> =>
    api
      .get<PreviewConfigResponse>(`${BASE}/${id}/preview/config`, {
        params: { organizationId, sessionId },
      })
      .then((r) => r.data),

  handlePreviewToolCall: (
    id: string,
    organizationId: string,
    sessionId: string,
    toolName: string,
    args: Record<string, unknown>,
  ): Promise<PreviewToolCallResponse> =>
    api
      .post<PreviewToolCallResponse>(
        `${BASE}/${id}/preview/tool-call`,
        { sessionId, toolName, args },
        { params: { organizationId } },
      )
      .then((r) => r.data),

  submitPreview: (
    id: string,
    organizationId: string,
    sessionId: string,
    transcript: PreviewTranscriptEntry[],
    toolCallLog: PreviewToolCallLogEntry[],
  ): Promise<void> =>
    api
      .post<void>(
        `${BASE}/${id}/preview/submit`,
        { sessionId, transcript, toolCallLog },
        { params: { organizationId } },
      )
      .then(() => undefined),

  getLatestPreviewSession: (
    id: string,
    organizationId: string,
  ): Promise<import('./types').LatestPreviewSession | null> =>
    api
      .get<import('./types').LatestPreviewSession | null>(`${BASE}/${id}/preview/sessions/latest`, {
        params: { organizationId },
      })
      .then((r) => r.data),

  listPreviewSessions: (
    id: string,
    organizationId: string,
  ): Promise<import('./types').LatestPreviewSession[]> =>
    api
      .get<import('./types').LatestPreviewSession[]>(`${BASE}/${id}/preview/sessions`, {
        params: { organizationId },
      })
      .then((r) => r.data ?? []),
};

const REGULATORY_BASE = '/voice-compliance/regulatory-bundles';

export const regulatoryBundlesApi = {
  list: (organizationId: string): Promise<import('./types').RegulatoryBundleSubmission[]> =>
    api
      .get<import('./types').RegulatoryBundleSubmission[]>(`${REGULATORY_BASE}/submissions`, {
        params: { organizationId },
      })
      .then((r) => r.data),

  listPending: (): Promise<import('./types').RegulatoryBundleSubmission[]> =>
    api
      .get<import('./types').RegulatoryBundleSubmission[]>(`${REGULATORY_BASE}/submissions/pending`)
      .then((r) => r.data),

  upload: (
    organizationId: string,
    args: {
      country: string;
      documentType: import('./types').RegulatoryDocumentType;
      file: File;
    },
  ): Promise<import('./types').RegulatoryBundleSubmission> => {
    const form = new FormData();
    form.append('file', args.file);
    form.append('country', args.country);
    form.append('documentType', args.documentType);
    return api
      .post<import('./types').RegulatoryBundleSubmission>(`${REGULATORY_BASE}/submissions`, form, {
        params: { organizationId },
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  updateStatus: (
    id: string,
    body: {
      status: import('./types').RegulatoryBundleStatus;
      reviewerNote?: string;
      twilioBundleSid?: string;
    },
  ): Promise<import('./types').RegulatoryBundleSubmission> =>
    api
      .patch<import('./types').RegulatoryBundleSubmission>(
        `${REGULATORY_BASE}/submissions/${id}`,
        body,
      )
      .then((r) => r.data),

  getDownloadUrl: (id: string, organizationId: string): Promise<{ url: string }> =>
    api
      .get<{ url: string }>(`${REGULATORY_BASE}/submissions/${id}/download`, {
        params: { organizationId },
      })
      .then((r) => r.data),
};

const COUNTRY_LANG_BASE = '/voice/country-language-mappings';

export const countryLanguageMappingsApi = {
  list: (): Promise<CountryLanguageMapping[]> =>
    api.get<CountryLanguageMapping[]>(COUNTRY_LANG_BASE).then((r) => r.data),

  create: (dto: CreateCountryLanguageMappingDto): Promise<CountryLanguageMapping> =>
    api.post<CountryLanguageMapping>(COUNTRY_LANG_BASE, dto).then((r) => r.data),

  update: (
    countryCode: string,
    dto: UpdateCountryLanguageMappingDto,
  ): Promise<CountryLanguageMapping> =>
    api
      .patch<CountryLanguageMapping>(`${COUNTRY_LANG_BASE}/${encodeURIComponent(countryCode)}`, dto)
      .then((r) => r.data),

  remove: (countryCode: string): Promise<void> =>
    api.delete(`${COUNTRY_LANG_BASE}/${encodeURIComponent(countryCode)}`).then(() => undefined),
};

const CALLS_BASE = '/voice-calls';

export const voiceCallsApi = {
  list: (
    organizationId: string,
    filters: ListVoiceCallsFilters = {},
  ): Promise<PaginatedVoiceCalls> =>
    api
      .get<PaginatedVoiceCalls>(CALLS_BASE, { params: { organizationId, ...filters } })
      .then((r) => r.data),

  get: (id: string, organizationId: string): Promise<VoiceCall> =>
    api.get<VoiceCall>(`${CALLS_BASE}/${id}`, { params: { organizationId } }).then((r) => r.data),

  update: (id: string, organizationId: string, dto: UpdateVoiceCallDto): Promise<VoiceCall> =>
    api
      .patch<VoiceCall>(`${CALLS_BASE}/${id}`, dto, { params: { organizationId } })
      .then((r) => r.data),

  reprocessAnalysis: (id: string, organizationId: string): Promise<ReprocessAnalysisResponse> =>
    api
      .post<ReprocessAnalysisResponse>(`${CALLS_BASE}/${id}/reprocess-analysis`, undefined, {
        params: { organizationId },
      })
      .then((r) => r.data),

  getTranscript: (id: string, organizationId: string): Promise<VoiceCallTranscriptEntry[]> =>
    api
      .get<VoiceCallTranscriptEntry[]>(`${CALLS_BASE}/${id}/transcript`, {
        params: { organizationId },
      })
      .then((r) => r.data),

  getAudio: (id: string, organizationId: string): Promise<AudioUrlResponse> =>
    api
      .get<AudioUrlResponse>(`${CALLS_BASE}/${id}/audio`, { params: { organizationId } })
      .then((r) => r.data),

  getReplay: (id: string, organizationId: string): Promise<ReplayData> =>
    api
      .get<ReplayData>(`${CALLS_BASE}/${id}/replay`, { params: { organizationId } })
      .then((r) => r.data),

  getStats: (organizationId: string): Promise<CallStats> =>
    api.get<CallStats>(`${CALLS_BASE}/stats`, { params: { organizationId } }).then((r) => r.data),

  cancel: (id: string, organizationId: string): Promise<{ cancelled: boolean }> =>
    api
      .post<{ cancelled: boolean }>(`${CALLS_BASE}/${id}/cancel`, undefined, {
        params: { organizationId },
      })
      .then((r) => r.data),

  retry: (id: string, organizationId: string): Promise<VoiceCall> =>
    api
      .post<VoiceCall>(`${CALLS_BASE}/${id}/retry`, undefined, { params: { organizationId } })
      .then((r) => r.data),

  remove: (id: string, organizationId: string): Promise<void> =>
    api.delete(`${CALLS_BASE}/${id}`, { params: { organizationId } }).then(() => undefined),

  trigger: ({ organizationId, ...body }: TriggerVoiceCallDto): Promise<VoiceCall> =>
    api
      .post<VoiceCall>(`${CALLS_BASE}/trigger`, body, {
        params: { organizationId },
      })
      .then((r) => r.data),

  triggerBatch: (
    organizationId: string,
    dto: TriggerBatchVoiceCallDto,
  ): Promise<import('./types').VoiceCallBatch> =>
    api
      .post<import('./types').VoiceCallBatch>(`${CALLS_BASE}/batch`, dto, {
        params: { organizationId },
      })
      .then((r) => r.data),

  getBatch: (id: string, organizationId: string): Promise<import('./types').VoiceCallBatch> =>
    api
      .get<import('./types').VoiceCallBatch>(`${CALLS_BASE}/batch/${id}`, {
        params: { organizationId },
      })
      .then((r) => r.data),

  cancelBatch: (id: string, organizationId: string): Promise<{ cancelled: boolean }> =>
    api
      .post<{ cancelled: boolean }>(`${CALLS_BASE}/batch/${id}/cancel`, undefined, {
        params: { organizationId },
      })
      .then((r) => r.data),
};
